// Public, passwordless contract-checklist chat. Reached via a per-submission
// link (/contract/:token). No login — the token is the credential. Talks to
// the public endpoints /api/contract-chat/:token[/reply]. Uses plain fetch so
// it never touches the app's auth interceptors.
import { useCallback, useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'

type Msg = { role: 'assistant' | 'submitter'; text: string }
type Submitter = { name: string; email: string }
type State = { status: string; closed: boolean; transcript: Msg[]; submitter?: Submitter }

// Accent/nav aren't in :root defaults (set at login-time theming); the public
// page never themes, so give them explicit fallbacks or bubbles go invisible.
const ACCENT = 'var(--me-accent, #2563eb)'
const NAV = 'var(--me-nav, #44403c)'
const MONO = 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace'

// Render backtick spans as monospace (the agent wraps the contract purpose in
// backticks). Normalize ``` fenced blocks to inline first, since the model
// sometimes uses a fence. Everything else is plain text.
function renderText(text: string) {
  const norm = text.replace(/```[^\n]*\n?([\s\S]*?)```/g, (_m, inner) => '`' + inner.trim() + '`')
  return norm.split(/(`[^`]+`)/g).map((p, i) =>
    p.length >= 2 && p.startsWith('`') && p.endsWith('`') ? (
      <code key={i} className="px-1.5 py-0.5 rounded text-[13.5px]"
            style={{ fontFamily: MONO, background: 'var(--me-bg)', border: '1px solid var(--me-border)' }}>
        {p.slice(1, -1)}
      </code>
    ) : (
      <span key={i}>{p}</span>
    ))
}

export default function ContractChat() {
  const { token } = useParams<{ token: string }>()
  const [state, setState] = useState<State | null>(null)
  const [notFound, setNotFound] = useState(false)
  const [draft, setDraft] = useState('')
  const [sending, setSending] = useState(false)
  const scrollRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)

  // Browser-tab label so worker-facing chat tabs are easy to tell apart.
  useEffect(() => { document.title = 'Kai' }, [])

  const load = useCallback(async () => {
    try {
      const r = await fetch(`/api/contract-chat/${token}/`)
      if (r.status === 404) { setNotFound(true); return }
      if (!r.ok) return
      setState(await r.json())
    } catch { /* transient */ }
  }, [token])

  useEffect(() => { load() }, [load])
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' })
  }, [state?.transcript.length])
  // Keep focus in the input as the conversation advances (no re-clicking).
  useEffect(() => {
    if (state && !state.closed && !sending) inputRef.current?.focus()
  }, [state?.transcript.length, state?.closed, sending])

  const send = async () => {
    const message = draft.trim()
    if (!message || sending || state?.closed) return
    setSending(true)
    // optimistic echo
    setState((s) => s ? { ...s, transcript: [...s.transcript, { role: 'submitter', text: message }] } : s)
    setDraft('')
    try {
      const r = await fetch(`/api/contract-chat/${token}/reply/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message }),
      })
      if (r.ok) setState(await r.json())
      else await load()
    } catch { await load() } finally {
      setSending(false)
      setTimeout(() => inputRef.current?.focus(), 0)
    }
  }

  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send() }
  }

  if (notFound) {
    return (
      <Shell>
        <div className="text-center text-[color:var(--me-textMute)] py-16 px-6">
          This link is not valid or has expired. If you believe this is an error,
          please reply to the email from the Office of General Counsel.
        </div>
      </Shell>
    )
  }

  return (
    <Shell aside={state ? <SubmitterCard submitter={state.submitter} /> : undefined}>
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-5 space-y-3">
        {!state && (
          <div className="grid place-items-center py-16">
            <div className="size-7 rounded-full border-[3px] border-[color:var(--me-textMute)] border-t-transparent animate-spin" />
          </div>
        )}
        {state?.transcript.map((m, i) => (
          <div key={i} className={m.role === 'submitter' ? 'flex justify-end' : 'flex justify-start'}>
            <div
              className="max-w-[80%] rounded-2xl px-4 py-2.5 text-[15px] leading-relaxed whitespace-pre-wrap"
              style={m.role === 'submitter'
                ? { background: ACCENT, color: '#fff' }
                : { background: 'var(--me-surface)', color: 'var(--me-text)', border: '1px solid var(--me-border)' }}
            >
              {renderText(m.text)}
            </div>
          </div>
        ))}
        {sending && (
          <div className="flex justify-start">
            {(state?.transcript.filter((m) => m.role === 'submitter').length ?? 0) === 1 ? (
              // After the first answer, surface what the system is doing (Ken).
              <div className="px-2 py-1 text-xs italic" style={{ color: 'var(--me-textMute)' }}>
                reading agreements, scanning for redlines and comments
              </div>
            ) : (
              <div className="rounded-2xl px-4 py-2.5" style={{ background: 'var(--me-surface)', border: '1px solid var(--me-border)' }}>
                <span className="inline-block size-1.5 rounded-full bg-[color:var(--me-textMute)] animate-pulse" />
              </div>
            )}
          </div>
        )}
      </div>

      <div className="border-t px-3 py-3" style={{ borderColor: 'var(--me-border)' }}>
        {state?.closed ? (
          <div className="text-center text-sm text-[color:var(--me-textMute)] py-2">
            {state.status === 'bounced'
              ? 'This submission needs another step before we can review it (see the message above).'
              : 'Thanks — this conversation is complete. You can close this window.'}
          </div>
        ) : (
          <div className="flex items-end gap-2">
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={onKey}
              rows={1}
              placeholder=""
              autoFocus
              disabled={!state}
              className="flex-1 resize-none rounded-xl px-3 py-2.5 text-[15px] outline-none"
              style={{ background: 'var(--me-surface)', color: 'var(--me-text)', border: '1px solid var(--me-border)' }}
            />
            <button
              onClick={send}
              disabled={!draft.trim() || sending || !state}
              className="rounded-xl px-4 py-2.5 text-sm font-medium text-white disabled:opacity-40"
              style={{ background: ACCENT }}
            >
              Send
            </button>
          </div>
        )}
      </div>
    </Shell>
  )
}

function SubmitterCard({ submitter }: { submitter?: Submitter }) {
  return (
    <div className="w-56 shrink-0 rounded-2xl shadow-lg overflow-hidden self-start"
         style={{ background: 'var(--me-panel)', border: '1px solid var(--me-border)' }}>
      <div className="px-4 py-4 border-b" style={{ borderColor: 'var(--me-border)', background: NAV }}>
        <div className="text-white font-semibold">Submitter</div>
      </div>
      <div className="px-4 py-4">
        <div className="font-medium" style={{ color: 'var(--me-text)' }}>{submitter?.name || '—'}</div>
        <div className="text-sm mt-1 break-all" style={{ color: 'var(--me-textMute)' }}>{submitter?.email || ''}</div>
        <label className="mt-6 flex items-center justify-center gap-2 text-sm cursor-pointer"
               style={{ color: 'var(--me-textMute)' }}>
          <input type="checkbox" className="size-4" />
          confirm
        </label>
      </div>
    </div>
  )
}

function Shell({ children, aside }: { children: React.ReactNode; aside?: React.ReactNode }) {
  return (
    <div className="min-h-screen w-full grid place-items-center px-3 py-6"
         style={{ background: 'var(--me-bg)', fontFamily: 'var(--me-font)' }}>
      <div className="flex items-start gap-3 w-full max-w-4xl justify-center">
        {aside}
        <div className="flex-1 max-w-2xl flex flex-col rounded-2xl shadow-lg overflow-hidden"
             style={{ height: 'min(80vh, 720px)', background: 'var(--me-panel)', border: '1px solid var(--me-border)' }}>
          <div className="px-5 py-3 border-b" style={{ borderColor: 'var(--me-border)', background: NAV }}>
            <div className="text-white font-semibold">Moravian University Office of the General Counsel</div>
            <div className="text-xs mt-0.5" style={{ color: 'rgba(255,255,255,0.8)' }}>Agreement Submission Chat</div>
          </div>
          {children}
        </div>
      </div>
    </div>
  )
}
